import type { AttributeField } from "@/features/properties/types/propertyAttributes";

/** Check if a leaf field has been answered (non-null, non-empty) */
export function isFieldAnswered(field: AttributeField): boolean {
  if (field.type === "node") {
    // A node is "answered" if ALL its children are answered
    return field.fields.every(isFieldAnswered);
  }
  const val = (field as any).value;
  if (val == null) return false;
  if (typeof val === "string" && val.trim() === "") return false;
  if (Array.isArray(val) && val.length === 0) return false;
  return true;
}

/**
 * Check if a field is required per the template's required set (keyed by
 * cleaned lowercase title). A node counts as required when ALL its leaves are,
 * so fully-required sections can be hidden along with their questions.
 */
export function isFieldRequired(field: AttributeField, requiredSet: Set<string>): boolean {
  if (field.type === "node") {
    return field.fields.length > 0 && field.fields.every((child) => isFieldRequired(child, requiredSet));
  }
  const cleanTitle = field.title.replace(/\s*\[.*?\]\s*$/, "").trim().toLowerCase();
  return requiredSet.has(cleanTitle);
}

/** Count total and answered leaf fields recursively */
export function countLeafFields(fields: AttributeField[]): { total: number; answered: number } {
  let total = 0;
  let answered = 0;
  for (const field of fields) {
    if (field.type === "node") {
      const sub = countLeafFields(field.fields);
      total += sub.total;
      answered += sub.answered;
    } else {
      total++;
      if (isFieldAnswered(field)) answered++;
    }
  }
  return { total, answered };
}

/** Count all photo URLs across the survey tree: PhotosField values + LeafMetadata.evidence */
export function countSurveyPhotos(fields: AttributeField[]): number {
  let n = 0;
  for (const field of fields) {
    if (field.type === "node") {
      n += countSurveyPhotos(field.fields);
      continue;
    }
    if (field.type === "photos" && Array.isArray(field.value)) {
      n += field.value.length;
    }
    const evidence = (field as { evidence?: unknown }).evidence;
    if (Array.isArray(evidence)) n += evidence.length;
  }
  return n;
}

/** Extract all leaf field ref_id → value pairs for backup */
export function extractFieldValues(
  fields: AttributeField[]
): Record<string, string | number | boolean | string[] | null> {
  const result: Record<string, any> = {};
  for (const field of fields) {
    if (field.type === "node") {
      Object.assign(result, extractFieldValues(field.fields));
    } else {
      result[field.id] = (field as any).value;
    }
  }
  return result;
}
